/**
 * Retry Utilities
 * Provides exponential backoff and retry logic for handling transient failures
 */

/**
 * Retry configuration interface
 */
export interface RetryConfig {
  /** Maximum number of retry attempts */
  maxRetries: number;
  /** Initial delay in milliseconds before first retry */
  initialDelayMs: number;
  /** Maximum delay in milliseconds (backoff cap) */
  maxDelayMs: number;
  /** Multiplier for exponential backoff (typically 2) */
  backoffMultiplier: number;
  /** Whether to add random jitter to prevent thundering herd */
  enableJitter: boolean;
}

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  enableJitter: true,
};

/**
 * Sleep/delay utility function
 * @param ms - Milliseconds to sleep
 * @returns Promise that resolves after the delay
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Calculate exponential backoff delay
 * @param attempt - Current retry attempt (0-based)
 * @param config - Retry configuration
 * @returns Delay in milliseconds
 */
export function calculateBackoffDelay(attempt: number, config: RetryConfig): number {
  const exponentialDelay = config.initialDelayMs * Math.pow(config.backoffMultiplier, attempt);
  const cappedDelay = Math.min(exponentialDelay, config.maxDelayMs);

  // Add jitter to prevent thundering herd (random 0-20% of delay)
  if (config.enableJitter) {
    const jitter = cappedDelay * 0.2 * Math.random();
    return cappedDelay + jitter;
  }

  return cappedDelay;
}

/**
 * Retry an async operation with exponential backoff
 * @param operation - Async function to retry
 * @param shouldRetry - Function to determine if error is retryable
 * @param config - Retry configuration
 * @param onRetry - Optional callback called before each retry
 * @returns Result of the operation
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  shouldRetry: (error: unknown, attempt: number) => boolean,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
  onRetry?: (attempt: number, delayMs: number, error: unknown) => void
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Don't retry if this was the last attempt
      if (attempt >= config.maxRetries) {
        break;
      }

      // Check if error is retryable
      if (!shouldRetry(error, attempt)) {
        throw error;
      }

      // Calculate delay for next retry
      const delayMs = calculateBackoffDelay(attempt, config);

      // Call retry callback if provided
      if (onRetry) {
        onRetry(attempt + 1, delayMs, error);
      }

      // Wait before retrying
      await sleep(delayMs);
    }
  }

  // All retries exhausted
  throw lastError;
}

/**
 * Check if HTTP status code is retryable
 * @param status - HTTP status code
 * @returns True if the status is retryable
 */
export function isRetryableHttpStatus(status: number | undefined): boolean {
  if (!status) return false;

  // Retryable status codes:
  // 408 - Request Timeout
  // 429 - Too Many Requests (rate limiting)
  // 500 - Internal Server Error
  // 502 - Bad Gateway
  // 503 - Service Unavailable
  // 504 - Gateway Timeout
  const retryableStatuses = [408, 429, 500, 502, 503, 504];
  return retryableStatuses.includes(status);
}

/**
 * Check if error is a network error (connection issue)
 * @param error - Error to check
 * @returns True if it's a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const err = error as { code?: string; message?: string };

  // Common network error codes
  const networkErrorCodes = [
    'ECONNRESET',
    'ECONNREFUSED',
    'ETIMEDOUT',
    'ENOTFOUND',
    'ENETUNREACH',
    'EAI_AGAIN',
  ];

  if (err.code && networkErrorCodes.includes(err.code)) {
    return true;
  }

  // Check for network-related messages
  if (err.message) {
    const networkKeywords = ['network', 'connection', 'timeout', 'ECONNRESET'];
    return networkKeywords.some((keyword) =>
      err.message!.toLowerCase().includes(keyword.toLowerCase())
    );
  }

  return false;
}

/**
 * Retry telemetry event data
 */
export interface RetryTelemetryData {
  operationName: string;
  attempt: number;
  maxAttempts: number;
  delayMs: number;
  errorType: string;
  errorMessage: string;
  success: boolean;
}

/**
 * Format retry attempt for logging/telemetry
 * @param attempt - Current attempt number (1-based)
 * @param maxAttempts - Maximum number of attempts
 * @param error - Error that triggered the retry
 * @param delayMs - Delay before next retry
 * @returns Formatted telemetry data
 */
export function formatRetryTelemetry(
  operationName: string,
  attempt: number,
  maxAttempts: number,
  error: unknown,
  delayMs: number
): RetryTelemetryData {
  const errorType = error instanceof Error ? error.constructor.name : 'UnknownError';
  const errorMessage = error instanceof Error ? error.message : String(error);

  return {
    operationName,
    attempt,
    maxAttempts,
    delayMs,
    errorType,
    errorMessage,
    success: false,
  };
}
